// Epoch 17: Leads Inbox List Component
// Displays list of lead responses with filtering and real-time updates
// Date: July 25, 2025

import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabase/client';
import { 
  LeadResponse, 
  LeadResponseFilters, 
  LeadResponsesListResponse,
  STATUS_BADGE_CONFIG
} from '../../types/lead-responses';
import { Campaign } from '../../types';

interface LeadsInboxListProps {
  onLeadSelect?: (leadResponse: LeadResponse) => void;
  selectedLeadId?: string;
  showFilters?: boolean;
  compact?: boolean;
}

const LeadsInboxList: React.FC<LeadsInboxListProps> = ({
  onLeadSelect,
  selectedLeadId,
  showFilters = true,
  compact = false
}) => {
  const [leadResponses, setLeadResponses] = useState<LeadResponse[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Filters state
  const [filters, setFilters] = useState<LeadResponseFilters>({
    status: 'all',
    campaign: 'all',
    search: '',
    date_from: '',
    date_to: ''
  });
  
  // Sort state - using default sort, no UI controls after removing column headers
  const [sortField] = useState<'created_at' | 'updated_at' | 'lead_name' | 'lead_company' | 'status'>('created_at');
  const [sortDirection] = useState<'asc' | 'desc'>('desc');
  
  const limit = compact ? 10 : 20;

  // Define functions first
  const loadCampaigns = async () => {
    try {
      const { data, error } = await supabase
        .from('campaigns')
        .select('id, name, status')
        .order('name');

      if (error) throw error;
      setCampaigns(data || []);
    } catch (error) {
      console.error('Error loading campaigns:', error);
    }
  };

  const loadLeadResponses = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError(null);

    try {
      const requestBody = {
        action: 'list',
        filters: {
          ...filters,
          page: currentPage,
          limit,
          sort_field: sortField,
          sort_direction: sortDirection
        }
      };

      const { data, error } = await supabase.functions.invoke('lead-responses', {
        body: requestBody
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const response = data as LeadResponsesListResponse;
      setLeadResponses(response.data || []);
      setTotalCount(response.count || 0);

    } catch (error) {
      console.error('Error loading lead responses:', error);
      setError(error.message || 'Failed to load lead responses');
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [filters, sortField, sortDirection, currentPage, limit]);

  // Load campaigns for filter dropdown
  useEffect(() => {
    loadCampaigns();
  }, []);

  // Load lead responses when filters or pagination changes
  useEffect(() => {
    loadLeadResponses();
  }, [filters, sortField, sortDirection, currentPage, loadLeadResponses]);

  // Auto-refresh every 30 seconds for real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      loadLeadResponses(false); // Don't show loading state on auto-refresh
    }, 30000);

    return () => clearInterval(interval);
  }, [loadLeadResponses]);

  const handleFilterChange = useCallback((field: keyof LeadResponseFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setCurrentPage(1); // Reset to first page when filters change
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 168) { // 7 days
      return date.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const getStatusBadge = (status: LeadResponse['status']) => {
    const config = STATUS_BADGE_CONFIG[status];
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const totalPages = Math.ceil(totalCount / limit);

  if (loading && (!leadResponses || leadResponses.length === 0)) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="animate-pulse space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex space-x-4">
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
              <div className="h-6 bg-gray-200 rounded w-20"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center text-red-600">
          <p className="font-medium">Error loading lead responses</p>
          <p className="text-sm mt-1">{error}</p>
          <button
            onClick={() => loadLeadResponses()}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">
            Lead Responses {totalCount > 0 && `(${totalCount})`}
          </h2>
          <button
            onClick={() => loadLeadResponses()}
            disabled={loading}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium disabled:opacity-50"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="space-y-4">
            {/* First Row: Search Bar (Full Width) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <input
                type="text"
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Lead name or company..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Second Row: Status and Campaign Filters */}
            <div className="grid md:grid-cols-2 gap-4">
              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  data-filter-status
                  value={filters.status || 'all'}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Statuses</option>
                  <option value="new">New</option>
                  <option value="read">Read</option>
                  <option value="reply_requested">Reply Requested</option>
                  <option value="reply_sent">Reply Sent</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              {/* Campaign Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Campaign</label>
                <select
                  value={filters.campaign || 'all'}
                  onChange={(e) => handleFilterChange('campaign', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Campaigns</option>
                  {campaigns.map(campaign => (
                    <option key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Third Row: Date Range Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
              <div className="flex space-x-2 max-w-md">
                <input
                  type="date"
                  value={filters.date_from || ''}
                  onChange={(e) => handleFilterChange('date_from', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="From"
                />
                <input
                  type="date"
                  value={filters.date_to || ''}
                  onChange={(e) => handleFilterChange('date_to', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="To"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* List Content */}
      <div className="divide-y divide-gray-200">
        {!leadResponses || leadResponses.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">
            <p className="text-lg font-medium">No lead responses found</p>
            <p className="text-sm mt-1">
              {Object.values(filters).some(v => v && v !== 'all') 
                ? 'Try adjusting your filters to see more results.'
                : 'Lead responses will appear here when CSRs submit them.'}
            </p>
          </div>
        ) : (
          (leadResponses || []).map((leadResponse) => (
            <div
              key={leadResponse.id}
              onClick={() => onLeadSelect?.(leadResponse)}
              className={`px-6 py-4 cursor-pointer transition-colors ${
                selectedLeadId === leadResponse.id
                  ? 'bg-blue-50 border-l-4 border-blue-600'
                  : 'hover:bg-gray-50'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  {/* Header row */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <h3 className="text-sm font-medium text-gray-900 truncate">
                        {leadResponse.lead_name}
                      </h3>
                      {getStatusBadge(leadResponse.status)}
                    </div>
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <span>{formatDate(leadResponse.created_at)}</span>
                      {leadResponse.reply_requests && leadResponse.reply_requests.length > 0 && (
                        <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                          {(leadResponse.reply_requests || []).filter(r => r && !r.sent_at).length} pending
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Company and Campaign info */}
                  <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                    <span className="font-medium">{leadResponse.lead_company}</span>
                    {leadResponse.lead_title && (
                      <span>• {leadResponse.lead_title}</span>
                    )}
                    {leadResponse.campaign && (
                      <span>• {leadResponse.campaign.name}</span>
                    )}
                  </div>

                  {/* Preview text */}
                  {!compact && (
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {leadResponse.csr_notes && leadResponse.csr_notes.length > 100 
                        ? `${leadResponse.csr_notes.substring(0, 100)}...`
                        : leadResponse.csr_notes || 'No notes available'}
                    </p>
                  )}

                  {/* LinkedIn profile link */}
                  <div className="mt-2">
                    <a
                      href={leadResponse.lead_linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800"
                    >
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                      View LinkedIn Profile
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {((currentPage - 1) * limit) + 1} to {Math.min(currentPage * limit, totalCount)} of {totalCount} results
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              {/* Page numbers */}
              <div className="flex space-x-1">
                {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                  const pageNum = Math.max(1, Math.min(currentPage - 2, totalPages - 4)) + i;
                  if (pageNum > totalPages) return null;
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-2 text-sm rounded-md ${
                        pageNum === currentPage
                          ? 'bg-blue-600 text-white'
                          : 'border border-gray-300 hover:bg-gray-100'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default LeadsInboxList;